import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Search } from "lucide-react";
import { PortalItem, fileUrl, formatMoney, portalApi } from "@/lib/catalog-api";
import { Wordmark } from "@/components/brand/Wordmark";

export default function Portal() {
  const { token } = useParams();
  const [info, setInfo] = useState<{ requires_password: boolean; workspace_name: string; workspace_logo_url?: string | null; title?: string | null } | null>(null);
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [items, setItems] = useState<PortalItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showValues, setShowValues] = useState(false);
  const [filters, setFilters] = useState<{ categories?: string[]; location_types?: string[]; locations?: { id: string; name: string }[] }>({});
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locationType, setLocationType] = useState("");
  const PAGE_SIZE = 48;

  useEffect(() => {
    if (!token) return;
    portalApi.info(token)
      .then((i) => { setInfo(i); if (!i.requires_password) setAuthed(true); })
      .catch(() => setNotFound(true));
  }, [token]);

  const fetchItems = useCallback(async (nextPage = 1) => {
    if (!token) return;
    try {
      const res = await portalApi.items(token, {
        password: password || undefined, q: q || undefined,
        category: category || undefined, location_id: locationId || undefined,
        location_type: locationType || undefined, page: nextPage, page_size: PAGE_SIZE,
      });
      setItems(res.items); setTotal(res.total); setPage(res.page);
      setShowValues(res.show_values);
      if (res.filters && Object.keys(res.filters).length) setFilters(res.filters);
      setAuthed(true); setAuthError(null);
    } catch (e: any) {
      if (e?.status === 401 || e?.message === "wrong_password") { setAuthError("Wrong password. Try again."); setAuthed(false); }
      else if (e?.status === 410) setAuthError("This link is no longer active.");
      else setAuthError("Could not load the portal.");
    }
  }, [token, password, q, category, locationId, locationType]);

  useEffect(() => { if (authed) fetchItems(1); /* eslint-disable-next-line */ }, [authed, q, category, locationId, locationType]);

  if (notFound) {
    return <main className="min-h-screen grid place-items-center bg-background px-6" data-testid="portal-notfound">
      <div className="text-center"><Lock className="w-7 h-7 mx-auto text-oxblood" /><h1 className="font-display text-3xl mt-4">This portal link doesn't exist.</h1></div>
    </main>;
  }

  if (!authed) {
    return (
      <main className="min-h-screen grid place-items-center bg-background px-6" data-testid="portal-gate">
        <form onSubmit={(e) => { e.preventDefault(); fetchItems(1); }} className="max-w-sm w-full border border-border p-8 bg-background">
          <Wordmark className="h-9 mb-6" />
          <Lock className="w-6 h-6 text-oxblood" />
          <h1 className="font-display text-2xl mt-4">{info?.workspace_name || "Wardrobe"} portal</h1>
          <p className="text-sm text-muted-foreground mt-2">Enter the password to view the collection.</p>
          <input type="password" data-testid="portal-password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-4 h-11 w-full border border-border bg-background px-3 text-sm" placeholder="Password" autoFocus />
          {authError && <p className="text-sm text-oxblood mt-2" data-testid="portal-auth-error">{authError}</p>}
          <button type="submit" data-testid="portal-enter"
            className="mt-4 h-11 w-full bg-oxblood hover:bg-oxblood-deep text-ivory text-sm uppercase tracking-[0.18em]">Enter</button>
        </form>
      </main>
    );
  }

  const pages = Math.ceil(total / PAGE_SIZE);
  return (
    <main className="min-h-screen bg-background" data-testid="portal-page">
      <header className="border-b border-border px-6 md:px-12 py-5 flex items-center gap-3">
        {info?.workspace_logo_url ? <img src={fileUrl(info.workspace_logo_url)} alt="" className="w-8 h-8 object-contain bg-bone" /> : null}
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">{info?.workspace_name}</p>
          <h1 className="font-display text-2xl leading-tight">{info?.title || "The Collection"}</h1>
        </div>
        <span className="ml-auto text-sm text-muted-foreground" data-testid="portal-total">{total} pieces</span>
        <Wordmark className="h-7 hidden sm:block ml-4" />
      </header>

      <div className="px-6 md:px-12 py-6 flex flex-wrap gap-3 items-center border-b border-border">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="portal-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, brand, box…" className="h-10 w-full border border-border bg-background pl-9 pr-3 text-sm" />
        </div>
        <select data-testid="portal-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 border border-border bg-background px-2 text-sm">
          <option value="">All categories</option>
          {(filters.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="portal-filter-location" value={locationId} onChange={(e) => setLocationId(e.target.value)} className="h-10 border border-border bg-background px-2 text-sm">
          <option value="">All locations</option>
          {(filters.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select data-testid="portal-filter-type" value={locationType} onChange={(e) => setLocationType(e.target.value)} className="h-10 border border-border bg-background px-2 text-sm">
          <option value="">All types</option>
          {(filters.location_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <section className="px-6 md:px-12 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="portal-items">
          {items.map((it, i) => (
            <article key={i} className="bg-background border border-border">
              <div className="aspect-[4/5] bg-bone overflow-hidden">
                {it.primary_photo_url
                  ? <img src={fileUrl(it.primary_photo_url)} alt={it.name} loading="lazy" className="w-full h-full object-contain" />
                  : <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">No photo</div>}
              </div>
              <div className="p-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{it.brand || it.category || "—"}</p>
                <p className="font-display text-base leading-snug mt-0.5 line-clamp-2">{it.name}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {[it.location_name, it.location_type].filter(Boolean).join(" · ") || "—"}
                  {it.box ? ` · Box ${it.box}` : ""}
                </p>
                {showValues && (it.insured_value || it.retail_value) ? (
                  <p className="text-xs mt-1">{formatMoney(it.insured_value || it.retail_value)}</p>
                ) : null}
              </div>
            </article>
          ))}
        </div>
        {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-16">No pieces match your filters.</p>}

        {pages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2 text-sm" data-testid="portal-pagination">
            <button disabled={page === 1} onClick={() => fetchItems(page - 1)} className="px-3 py-1.5 border border-border disabled:opacity-30">Prev</button>
            <span className="text-muted-foreground">Page {page} of {pages}</span>
            <button disabled={page >= pages} onClick={() => fetchItems(page + 1)} className="px-3 py-1.5 border border-border disabled:opacity-30">Next</button>
          </div>
        )}
      </section>
    </main>
  );
}
